import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Login from "./components/login";
import Navigation from "./components/Navigation";
import ContinueAssessment from "./components/continue";
import Remainders from "./components/remainder";
import Summaries from "./components/summaries";
import Header from "./components/Header";
import GoalsPage from "./components/GoalsPage";
import Editgoal from "./components/Editgoal";
import Startassessment from "./components/start-assessment";
import Help from "./components/help";
import Summarypage from "./components/summarypage";
import FeedbackPage from "./components/FeedbackPage";
import UpdateGoal from "./components/UpdateGoal";
import DemographicsData from "./components/DemographicsData";
import BackgroundInfoData from "./components/BackgroundInfoData";
import GuideLibrary from "./components/GuideLibrary";
import ReminderPopup from "./components/ReminderPopup";


import "./App.css";
import Footer from "./components/footer";
import Interventionspage from "./components/Interventions";
import GoalsetDownloadpage from "./components/goalsetdownload";
import GoalReviewpage from "./components/goalreview";

import ReviewStartassessment from "./components/Review-assessment";
import HistoricalPathway from "./components/HistoricalPathway";
import ClientHistory from "./components/ClientHistory";
import ProtectedRoute from "./components/ProtectedRoute";
import URLLockGuard from "./components/URLLockGuard";
import AssessmentLeaveGuard from "./components/AssessmentLeaveGuard";

// Human-readable page names used for the browser tab and for the screen-reader
// route announcement. These deliberately never expose route slugs such as
// "goalreview" or "start-assessment" - a screen-reader user hears the name of
// the page, exactly as it appears on screen.
const PAGE_TITLES = {
  "/": "Login",
  "/Home": "Home",
  // Legacy links (for example the Summary page's "Previous page" control)
  // still point at /dashboard. It maps onto Home so the therapist never lands
  // on an unnamed, empty page.
  "/dashboard": "Home",
  "/review-assessment": "Review Assessment",
  "/continue-assessment": "Continue Assessment",
  "/Suggested-interventions": "Suggested Interventions",
  "/Goals": "Review Existing Goals",
  "/Reminder": "My Reminders",
  "/Editgoal": "Edit Goal",
  "/help": "Help",
  "/Library": "Library",
  "/summarypage": "Summary",
  "/Interventions": "Suggested Interventions",
  "/FeedbackPage": "Feedback",
  "/updategoal": "Update Goal",
  "/goalsetdownload": "Intervention Planning",
  "/goalreview": "Goal Review",
  "/goalreview-history": "Goal Review History",
  "/demographics-data": "Demographics Data",
  "/background-info-data": "Background Information and Assessments Data",
  "/historical-pathway": "Historical Pathway",
  "/client-history": "Goal Review Records",
};

const getPageTitle = (location) => {
  if (location.pathname === "/start-assessment") {
    const appointment = (new URLSearchParams(location.search).get("appointment") || "").toLowerCase();
    if (appointment.includes("review") && appointment.includes("smart")) return "Review SMART Goal";
    if (appointment.includes("review")) return "Set New Goal for Existing Client";
    return "Start Initial Client Assessment";
  }
  return PAGE_TITLES[location.pathname] || "CPA Therapist";
};

function AppContent() {
  const mainContentRef = useRef(null);
  const location = useLocation();
  const isLoginPage = location.pathname === "/";
  const routeTitle = getPageTitle(location);

  // Only react to real page changes. Assessment screens change their query
  // string as the therapist moves through steps; that must not re-announce the
  // page or pull focus back to the heading.
  const appointmentKey = location.pathname === "/start-assessment"
    ? (new URLSearchParams(location.search).get("appointment") || "")
    : "";
  const routeKey = `${location.pathname}?${appointmentKey}`;

  const isFirstRender = useRef(true);
  const [routeAnnouncement, setRouteAnnouncement] = useState("");

  // Set the document title so every page is identified correctly (WCAG 2.4.2)
  // and, on a client-side route change, move focus to the page's <h1>. This is
  // what makes a screen reader announce e.g. "Home, heading level 1" instead of
  // reading the shell title or a developer route name.
  useEffect(() => {
    document.title = `${routeTitle} | CPA Clinical Pathway Application`;

    const heading = mainContentRef.current?.querySelector("h1");
    if (heading) {
      if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: false });
    } else if (mainContentRef.current) {
      mainContentRef.current.focus({ preventScroll: false });
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setRouteAnnouncement(`${routeTitle} page loaded`);
    }
    // routeKey intentionally drives this effect: query-string step changes on
    // the assessment screens must not count as a new page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  const handleSkip = (e) => {
    e.preventDefault();
    // On the Home page, skip directly to the "Start Initial Client Assessment" card
    if (location.pathname === "/Home" || location.pathname === "/dashboard") {
      const initialCard =
        document.getElementById("start-initial-client-assessment") ||
        mainContentRef.current?.querySelector(".feature-card-link");
      if (initialCard) {
        initialCard.focus();
        initialCard.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }
    }
    // On other pages (e.g. Reminder, Start Assessment), move focus to the primary heading or main container
    const heading = mainContentRef.current?.querySelector("h1");
    if (heading) {
      if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: false });
      heading.scrollIntoView({ block: "start", behavior: "smooth" });
    } else if (mainContentRef.current) {
      mainContentRef.current.focus({ preventScroll: false });
      mainContentRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  };

  return (
    <div className="app-container">

      {/* Skip Navigation Link - WCAG SC 2.4.1 */}
      <a
        href={location.pathname === "/Home" || location.pathname === "/dashboard" ? "#start-initial-client-assessment" : "#main-content"}
        className="skip-link"
        onClick={handleSkip}
      >
        Skip to main content
      </a>

      {/* One banner landmark for the whole app. It is outside <main> so
          screen readers do not announce the navigation twice or nest the
          header inside the main landmark. */}
      {!isLoginPage && <Header />}

      {/* Exactly ONE main landmark per page. Page components must not render
          their own <main>; a second one makes screen readers announce
          "main landmark 1", "main landmark 2" and similar noise. */}
      <main
        className="main-content"
        id="main-content"
        ref={mainContentRef}
        tabIndex={-1}
      >
        <AssessmentLeaveGuard />
        <URLLockGuard>
          <Routes>
            <Route path="/" element={<Login />} />

            <Route path="/Home" element={<ProtectedRoute><Navigation /></ProtectedRoute>} />
            <Route path="/dashboard" element={<Navigate to="/Home" replace />} />
            <Route path="/start-assessment" element={<ProtectedRoute><Startassessment /></ProtectedRoute>} />
            <Route path="/review-assessment" element={<ProtectedRoute><ReviewStartassessment /></ProtectedRoute>} />
            <Route path="/continue-assessment" element={<ProtectedRoute><ContinueAssessment /></ProtectedRoute>} />
            <Route path="/Suggested-interventions" element={<ProtectedRoute><Summaries /></ProtectedRoute>} />
            <Route path="/Goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
            <Route path="/Reminder" element={<ProtectedRoute><Remainders /></ProtectedRoute>} />
            <Route path="/Editgoal" element={<ProtectedRoute><Editgoal /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/Library" element={<ProtectedRoute><GuideLibrary /></ProtectedRoute>} />
            <Route path="/summarypage" element={<ProtectedRoute><Summarypage /></ProtectedRoute>} />
            <Route path="/Interventions" element={<ProtectedRoute><Interventionspage /></ProtectedRoute>} />
            <Route path="/FeedbackPage" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
            <Route path="/updategoal" element={<ProtectedRoute><UpdateGoal /></ProtectedRoute>} />
            <Route path="/goalsetdownload" element={<ProtectedRoute><GoalsetDownloadpage /></ProtectedRoute>} />
            <Route path="/goalreview" element={<ProtectedRoute><GoalReviewpage /></ProtectedRoute>} />
            <Route path="/goalreview-history" element={<ProtectedRoute><GoalReviewpage /></ProtectedRoute>} />
            <Route path="/demographics-data" element={<ProtectedRoute><DemographicsData /></ProtectedRoute>} />
            <Route path="/background-info-data" element={<ProtectedRoute><BackgroundInfoData /></ProtectedRoute>} />
            <Route path="/historical-pathway" element={<ProtectedRoute><HistoricalPathway /></ProtectedRoute>} />
            <Route path="/client-history" element={<ProtectedRoute><ClientHistory /></ProtectedRoute>} />
          </Routes>
        </URLLockGuard>
      </main>

      {/* WCAG 4.1.3 Status Messages - announces client-side page changes once */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {routeAnnouncement}
      </div>

      {/* Footer - Hidden on Login page */}
      {!isLoginPage && <Footer />}

      {/* Reminder Popup - only for logged-in pages, never on the login screen */}
      {!isLoginPage && <ReminderPopup />}

    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
